export const STANDARD_WIZARD_STEPS = [
  { key: "basic", label: "Basic Info" },
  { key: "header", label: "Header" },
  { key: "body", label: "Body" },
  { key: "buttons", label: "Buttons" },
  { key: "variables", label: "Variables" },
  { key: "review", label: "Review & Submit" },
];

export const CATALOG_WIZARD_STEPS = [
  { key: "basic", label: "Basic Info" },
  { key: "catalog", label: "Catalog" },
  { key: "review", label: "Review & Submit" },
];

export const CAROUSEL_WIZARD_STEPS = [
  { key: "basic", label: "Basic Info" },
  { key: "cards", label: "Cards" },
  { key: "review", label: "Review & Submit" },
];

export function getWizardSteps(templateType) {
  if (templateType === "CATALOG") return CATALOG_WIZARD_STEPS;
  if (templateType === "CAROUSEL") return CAROUSEL_WIZARD_STEPS;
  return STANDARD_WIZARD_STEPS;
}

export const TEMPLATE_TYPES = [
  {
    value: "STANDARD",
    label: "Standard",
    description: "Text, media, buttons",
    icon: "solar:document-text-linear",
    accent: "standard",
  },
  {
    value: "CAROUSEL",
    label: "Carousel",
    description: "Multiple image cards",
    icon: "solar:widget-4-linear",
    accent: "carousel",
  },
  {
    value: "CATALOG",
    label: "Catalog",
    description: "Product catalog message",
    icon: "solar:bag-4-linear",
    accent: "catalog",
  },
];

export const TEMPLATE_LANGUAGES = [
  { value: "en", label: "English" },
  { value: "hi", label: "Hindi" },
  { value: "gu", label: "Gujarati" },
  { value: "mr", label: "Marathi" },
  { value: "ta", label: "Tamil" },
  { value: "te", label: "Telugu" },
];

export const TEMPLATE_CATEGORIES = [
  { value: "UTILITY", label: "Utility" },
  { value: "MARKETING", label: "Marketing" },
  { value: "AUTHENTICATION", label: "Authentication" },
];

export function createEmptyCarouselCard() {
  return {
    id: `card-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    imageFile: null,
    bodyText: "",
    buttons: [],
  };
}

export const EMPTY_TEMPLATE_FORM = {
  templateType: "STANDARD",
  name: "",
  language: "en",
  category: "UTILITY",
  headerFormat: "NONE",
  headerText: "",
  headerMediaFile: null,
  bodyText: "",
  footerText: "",
  buttonMode: "NONE",
  quickReplyButtons: [],
  ctaButtons: [],
  variableExamples: {},
  catalogThumbnailFile: null,
  carouselButtonType: "QUICK_REPLY",
  carouselCards: [createEmptyCarouselCard(), createEmptyCarouselCard()],
};

export function normalizeTemplateName(value) {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

export function extractTemplateVariables(...texts) {
  const found = new Set();
  texts.forEach((text) => {
    const source = String(text || "");
    const matches = source.matchAll(/\{\{(\d+)\}\}/g);
    for (const match of matches) {
      found.add(match[1]);
    }
  });
  return [...found].sort((a, b) => Number(a) - Number(b));
}

export function insertVariableAtCursor(text, cursor, variableNum) {
  const token = `{{${variableNum}}}`;
  const start = cursor ?? text.length;
  const next = text.slice(0, start) + token + text.slice(start);
  return { text: next, cursor: start + token.length };
}

export function nextVariableIndex(...texts) {
  const vars = extractTemplateVariables(...texts);
  if (!vars.length) return 1;
  return Math.max(...vars.map(Number)) + 1;
}

export function renderBodyWithExamples(bodyText, examples = {}) {
  return String(bodyText || "").replace(/\{\{(\d+)\}\}/g, (_, num) => {
    const sample = examples[num];
    return sample ? sample : `{{${num}}}`;
  });
}

function buildStandardComponents(form, mediaHandle) {
  const components = [];
  const headerFormat = form.headerFormat;

  if (headerFormat === "TEXT" && form.headerText.trim()) {
    const headerComp = {
      type: "HEADER",
      format: "TEXT",
      text: form.headerText.trim(),
    };
    const headerVars = extractTemplateVariables(form.headerText);
    if (headerVars.length) {
      headerComp.example = {
        header_text: headerVars.map(
          (v) => form.variableExamples[v] || `example_${v}`,
        ),
      };
    }
    components.push(headerComp);
  } else if (headerFormat === "MEDIA" && mediaHandle) {
    components.push({
      type: "HEADER",
      format: "IMAGE",
      example: { header_handle: [mediaHandle] },
    });
  }

  if (form.bodyText.trim()) {
    const bodyComp = {
      type: "BODY",
      text: form.bodyText.trim(),
    };
    const bodyVars = extractTemplateVariables(form.bodyText);
    if (bodyVars.length) {
      bodyComp.example = {
        body_text: [
          bodyVars.map((v) => form.variableExamples[v] || `example_${v}`),
        ],
      };
    }
    components.push(bodyComp);
  }

  if (form.footerText.trim()) {
    components.push({ type: "FOOTER", text: form.footerText.trim() });
  }

  if (form.buttonMode === "QUICK_REPLY" && form.quickReplyButtons.length) {
    components.push({
      type: "BUTTONS",
      buttons: form.quickReplyButtons
        .filter((b) => b.text?.trim())
        .map((b) => ({
          type: "QUICK_REPLY",
          text: b.text.trim(),
        })),
    });
  } else if (form.buttonMode === "CALL_TO_ACTION" && form.ctaButtons.length) {
    const buttons = form.ctaButtons
      .filter((b) => b.text?.trim())
      .map((b) => {
        if (b.type === "PHONE") {
          return {
            type: "PHONE_NUMBER",
            text: b.text.trim(),
            phone_number: String(b.phone || "").trim(),
          };
        }
        return {
          type: "URL",
          text: b.text.trim(),
          url: String(b.url || "").trim(),
        };
      });
    if (buttons.length) {
      components.push({ type: "BUTTONS", buttons });
    }
  }

  return components;
}

function buildCatalogComponents(form, thumbnailHandle) {
  const components = [];

  if (thumbnailHandle) {
    components.push({
      type: "HEADER",
      format: "IMAGE",
      example: { header_handle: [thumbnailHandle] },
    });
  }

  if (form.bodyText.trim()) {
    components.push({ type: "BODY", text: form.bodyText.trim() });
  }

  if (form.footerText.trim()) {
    components.push({ type: "FOOTER", text: form.footerText.trim() });
  }

  components.push({
    type: "BUTTONS",
    buttons: [{ type: "CATALOG", text: "View Catalog" }],
  });

  return components;
}

function buildCarouselCardButton(button, buttonType) {
  if (buttonType === "QUICK_REPLY") {
    return { type: "QUICK_REPLY", text: button.text.trim() };
  }
  if (buttonType === "PHONE") {
    return {
      type: "PHONE_NUMBER",
      text: button.text.trim(),
      phone_number: String(button.phone || "").trim(),
    };
  }
  return {
    type: "URL",
    text: button.text.trim(),
    url: String(button.url || "").trim(),
  };
}

function buildCarouselComponents(form, cardMediaHandles = []) {
  const components = [];

  if (form.bodyText.trim()) {
    components.push({ type: "BODY", text: form.bodyText.trim() });
  }

  const cards = (form.carouselCards || []).map((card, idx) => {
    const cardButtons = (card.buttons || [])
      .filter((b) => b.text?.trim())
      .map((b) => buildCarouselCardButton(b, form.carouselButtonType));

    const entry = {
      components: [],
    };

    const handle = cardMediaHandles[idx];
    if (handle) {
      entry.components.push({
        type: "HEADER",
        format: "IMAGE",
        example: { header_handle: [handle] },
      });
    }

    if (card.bodyText?.trim()) {
      entry.components.push({
        type: "BODY",
        text: card.bodyText.trim(),
      });
    }

    if (cardButtons.length) {
      entry.components.push({
        type: "BUTTONS",
        buttons: cardButtons,
      });
    }

    return entry;
  });

  components.push({
    type: "CAROUSEL",
    cards,
  });

  return components;
}

export function buildTemplateComponents(form, mediaHandles = {}) {
  if (form.templateType === "CATALOG") {
    return buildCatalogComponents(form, mediaHandles.catalogThumbnail);
  }
  if (form.templateType === "CAROUSEL") {
    return buildCarouselComponents(form, mediaHandles.carouselCards || []);
  }
  return buildStandardComponents(form, mediaHandles.header);
}

export function validateWizardStep(stepKey, form) {
  const errors = [];

  if (stepKey === "basic") {
    if (!normalizeTemplateName(form.name)) {
      errors.push("Template name is required (letters, numbers, underscores only)");
    }
  }

  if (stepKey === "header") {
    if (form.headerFormat === "TEXT" && !form.headerText.trim()) {
      errors.push("Header text is required");
    }
    if (form.headerFormat === "TEXT" && form.headerText.length > 60) {
      errors.push("Header text maximum 60 characters");
    }
    if (form.headerFormat === "MEDIA" && !form.headerMediaFile) {
      errors.push("Select a media file for the header");
    }
  }

  if (stepKey === "body") {
    if (!form.bodyText.trim()) {
      errors.push("Message body is required");
    }
    if (form.bodyText.length > 1024) {
      errors.push("Message body maximum 1024 characters");
    }
    if (form.footerText.length > 60) {
      errors.push("Footer maximum 60 characters");
    }
  }

  if (stepKey === "catalog") {
    if (!form.bodyText.trim()) {
      errors.push("Message body is required");
    }
    if (form.bodyText.length > 1024) {
      errors.push("Message body maximum 1024 characters");
    }
    if (form.footerText.length > 60) {
      errors.push("Footer maximum 60 characters");
    }
  }

  if (stepKey === "buttons") {
    if (form.buttonMode === "QUICK_REPLY") {
      if (form.quickReplyButtons.length > 3) {
        errors.push("Maximum 3 quick reply buttons allowed");
      }
      form.quickReplyButtons.forEach((b, i) => {
        if (!b.text?.trim()) errors.push(`Quick reply button ${i + 1} text is required`);
        if (b.text?.length > 20) errors.push(`Quick reply button ${i + 1} max 20 characters`);
      });
    }
    if (form.buttonMode === "CALL_TO_ACTION") {
      if (form.ctaButtons.length > 2) {
        errors.push("Maximum 2 call-to-action buttons allowed");
      }
      form.ctaButtons.forEach((b, i) => {
        if (!b.text?.trim()) errors.push(`Button ${i + 1} text is required`);
        if (b.text?.length > 20) errors.push(`Button ${i + 1} max 20 characters`);
        if (b.type === "PHONE" && !b.phone?.trim()) {
          errors.push(`Button ${i + 1} phone number is required`);
        }
        if (b.type === "URL" && !b.url?.trim()) {
          errors.push(`Button ${i + 1} URL is required`);
        }
      });
    }
  }

  if (stepKey === "variables") {
    const vars = extractTemplateVariables(form.bodyText, form.headerText);
    vars.forEach((v) => {
      if (!String(form.variableExamples[v] || "").trim()) {
        errors.push(`Example for {{${v}}} is required`);
      }
    });
  }

  if (stepKey === "cards") {
    const cards = form.carouselCards || [];
    if (cards.length < 2) {
      errors.push("Minimum 2 cards required");
    }
    if (cards.length > 10) {
      errors.push("Maximum 10 cards allowed");
    }
    if (!form.bodyText.trim()) {
      errors.push("Message body above cards is required");
    }
    if (form.bodyText.length > 1024) {
      errors.push("Message body maximum 1024 characters");
    }
    cards.forEach((card, i) => {
      if (!card.imageFile) {
        errors.push(`Card ${i + 1}: image is required`);
      }
      if (!card.bodyText?.trim()) {
        errors.push(`Card ${i + 1}: body text is required`);
      }
      if (card.bodyText?.length > 160) {
        errors.push(`Card ${i + 1}: body max 160 characters`);
      }
      if (card.buttons?.length > 2) {
        errors.push(`Card ${i + 1}: maximum 2 buttons`);
      }
      card.buttons?.forEach((btn, bi) => {
        if (!btn.text?.trim()) {
          errors.push(`Card ${i + 1} button ${bi + 1}: text required`);
        }
        if (form.carouselButtonType === "PHONE" && !btn.phone?.trim()) {
          errors.push(`Card ${i + 1} button ${bi + 1}: phone required`);
        }
        if (form.carouselButtonType === "URL" && !btn.url?.trim()) {
          errors.push(`Card ${i + 1} button ${bi + 1}: URL required`);
        }
      });
    });
  }

  return errors;
}

// Backward compat
export const TEMPLATE_WIZARD_STEPS = STANDARD_WIZARD_STEPS;
